import { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    Chip,
    Divider,
    LinearProgress,
    Tooltip,
    Stack,
    Card,
    CircularProgress,
} from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import { Bookmark, Share2, Star } from "lucide-react";
import { Printer, Mail, Link2, X } from "lucide-react";
import TuneIcon from "@mui/icons-material/Tune";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { getTopStocks } from "../../../api/api";
import { useNavigate } from "react-router-dom";
import { getCurrencySymbol, fmtL } from "../../../utils/currency"; // ← adjust path

/* ---------------- helpers ---------------- */

const ratingColor = (value) => {
    if (value >= 4) return "#1DB954";
    if (value >= 3) return "#F5A623";
    return "#D0021B";
};

const FILTER_OPTIONS = {
    quantRating: ["STRONG BUY", "BUY", "HOLD", "SELL"],
    saRating: ["STRONG BUY", "BUY", "HOLD", "SELL"],
    wallStRating: ["STRONG BUY", "BUY", "HOLD", "SELL"],
    valuation: ["A+", "A", "A-", "B+", "B", "B-"],
    growth: ["A+", "A", "A-", "B+", "B", "B-"],
    profitability: ["A+", "A", "A-", "B+", "B", "B-"],
    momentum: ["A+", "A", "A-", "B+", "B", "B-"],
    epsRevisions: ["A+", "A", "A-", "B+", "B", "B-"],
};

const FILTER_LABELS = {
    quantRating: "FA Rating",
    saRating: "SA Analysts Rating",
    wallStRating: "Wall St. Analysts Rating",
    valuation: "Valuation",
    growth: "Growth",
    profitability: "Profitability",
    momentum: "Momentum",
    epsRevisions: "EPS Revisions",
};

/* ---------------- Filter Select ---------------- */

const FilterSelect = ({ label, options, selected, onToggle, onClear }) => {
    return (
        <Box>
            <Typography fontSize="var(--font-size-sm)" mb={0.5}>
                {label}
            </Typography>

            <Box
                sx={{
                    border: "1px solid #D1D5DB",
                    borderRadius: 1,
                    p: 1,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.8,
                    minHeight: 44,
                }}
            >
                {options.map((opt) => {
                    const active = selected.includes(opt);
                    return (
                        <Chip
                            key={opt}
                            label={opt}
                            size="small"
                            onClick={() => onToggle(opt)}
                            sx={{
                                cursor: "pointer",
                                fontWeight: 600,
                                background: active ? "#1F3D1A" : "#E5E7EB",
                                color: active ? "#fff" : "#111",
                            }}
                        />
                    );
                })}

                <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
                    <X size={14} style={{ cursor: "pointer" }} onClick={onClear} />
                </Box>
            </Box>
        </Box>
    );
};

/* ---------------- Stock Card Component ---------------- */

const StockCard = ({ stock, rank }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const sym = getCurrencySymbol();

    const getColorForGrade = (grade) => {
        if (!grade) return "#64748b";
        if (grade.startsWith('A')) return "#10b981";
        if (grade.startsWith('B')) return "#3b82f6";
        if (grade.startsWith('C')) return "#f59e0b";
        if (grade.startsWith('D')) return "#fb923c";
        if (grade === 'F') return "#ef4444";
        return "#64748b";
    };

    const gradeToScore = (grade) => {
        if (!grade) return 0;
        const gradeMap = {
            'A+': 10, 'A': 9.5, 'A-': 9,
            'B+': 8.5, 'B': 8, 'B-': 7.5,
            'C+': 7, 'C': 6.5, 'C-': 6,
            'D+': 5.5, 'D': 5, 'D-': 4.5,
            'F': 4
        };
        return gradeMap[grade] || 0;
    };

    const calculatePulseScore = (quantRating) => {
        // Map 1-5 rating to -100 to +100 scale
        if (!quantRating) return 0;
        return Math.round((quantRating - 3) * 50);
    };

    const getRatingText = (value) => {
        if (value >= 4.5) return "STRONG BUY";
        if (value >= 3.5) return "BUY";
        if (value >= 2.5) return "HOLD";
        return "SELL";
    };

    const pulseScore = calculatePulseScore(stock.quant_rating);
    const isBullish = pulseScore > 0;

    return (
        <Card
            sx={{
                bgcolor: "#1a2332",
                border: "1px solid #2d3748",
                borderRadius: 2,
                p: 2.5,
                transition: "all 0.3s ease",
                position: "relative",
                "&:hover": {
                    borderColor: "#3b82f6",
                    boxShadow: "0 4px 12px rgba(59, 130, 246, 0.15)",
                },
            }}
        >
            {/* Rank Badge */}
            <Box
                sx={{
                    position: "absolute",
                    top: 12,
                    left: 12,
                    bgcolor: "#3b82f6",
                    color: "#fff",
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 14,
                    fontWeight: 700,
                }}
            >
                {rank}
            </Box>

            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2.5} mt={2}>
                <Box>
                    <Typography
                        fontSize={28}
                        fontWeight={800}
                        color="#fff"
                        letterSpacing="-0.02em"
                        sx={{
                            cursor: "pointer",
                            "&:hover": { color: "#3b82f6" },
                        }}
                        onClick={() => navigate(`/stocks/${stock.symbol}/details`)}
                    >
                        {stock.symbol}
                    </Typography>
                    <Typography fontSize="var(--font-size-xs2)" color="#64748b" mb={1}>
                        {stock.company_name}
                    </Typography>
                    <Box display="flex" gap={0.75} mt={1}>
                        <Button
                            size="small"
                            startIcon={<AddIcon sx={{ fontSize: 12 }} />}
                            sx={{
                                bgcolor: "#3b82f6",
                                color: "#fff",
                                fontSize: 10,
                                fontWeight: 600,
                                textTransform: "uppercase",
                                px: 1.25,
                                py: 0.35,
                                minWidth: "auto",
                                borderRadius: 0.75,
                                "&:hover": { bgcolor: "#2563eb" },
                            }}
                        >
                            Portfolio
                        </Button>
                        <Chip
                            icon={<CheckIcon sx={{ fontSize: 12 }} />}
                            label="ADDED"
                            size="small"
                            sx={{
                                bgcolor: "transparent",
                                border: "1px solid #10b981",
                                color: "#10b981",
                                height: 22,
                                fontSize: 9,
                                fontWeight: 600,
                                "& .MuiChip-icon": { color: "#10b981" },
                            }}
                        />
                    </Box>
                </Box>

                <Box textAlign="right">
                    <Button
                        sx={{
                            bgcolor: ratingColor(stock.quant_rating),
                            color: "#fff",
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            px: 2,
                            py: 0.75,
                            borderRadius: 1,
                            mb: 1,
                            "&:hover": {
                                opacity: 0.9,
                            },
                        }}
                    >
                        {getRatingText(stock.quant_rating)}
                    </Button>
                    <Box>
                        <Typography fontSize={24} fontWeight={700} color="#fff" lineHeight={1}>
                            {sym}{stock.price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                        <Typography
                            fontSize="var(--font-size-xs2)"
                            fontWeight={600}
                            color={stock.change_percent >= 0 ? "#10b981" : "#ef4444"}
                        >
                            {stock.change_percent >= 0 ? "+" : ""}{stock.change_percent?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Ratings Row */}
            <Box display="flex" gap={1} mb={2}>
                <Chip
                    label="FA INSIGHT"
                    size="small"
                    sx={{
                        bgcolor: "#334155",
                        color: "#94a3b8",
                        fontSize: 9,
                        height: 20,
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                    }}
                />
                {isBullish ? (
                    <Chip
                        label={`Bullish (+${pulseScore})`}
                        size="small"
                        sx={{
                            bgcolor: "#10b981",
                            color: "#fff",
                            fontSize: 9,
                            height: 20,
                            fontWeight: 700,
                        }}
                    />
                ) : (
                    <Chip
                        label={`Bearish (${pulseScore})`}
                        size="small"
                        sx={{
                            bgcolor: "#ef4444",
                            color: "#fff",
                            fontSize: 9,
                            height: 20,
                            fontWeight: 700,
                        }}
                    />
                )}
            </Box>

            {/* Pulse Analysis Slider */}
            <Box mb={3}>
                <Box display="flex" justifyContent="space-between" mb={0.75}>
                    <Typography fontSize={9} fontWeight={700} color="#64748b" letterSpacing="0.05em">
                        BEARISH
                    </Typography>
                    <Typography fontSize={9} fontWeight={700} color="#64748b" letterSpacing="0.05em">
                        PULSE ANALYSIS
                    </Typography>
                    <Typography fontSize={9} fontWeight={700} color="#64748b" letterSpacing="0.05em">
                        BULLISH
                    </Typography>
                </Box>
                <Box position="relative">
                    <Box
                        sx={{
                            height: 8,
                            borderRadius: 1,
                            background: "linear-gradient(to right, #ef4444 0%, #64748b 50%, #10b981 100%)",
                        }}
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: `${((pulseScore + 100) / 200) * 100}%`,
                            transform: "translate(-50%, -50%)",
                            width: 20,
                            height: 20,
                            bgcolor: "#fff",
                            borderRadius: "50%",
                            border: "3px solid #3b82f6",
                            boxShadow: "0 0 0 4px rgba(59, 130, 246, 0.2)",
                        }}
                    />
                </Box>
                <Box display="flex" justifyContent="space-between" mt={0.5}>
                    <Typography fontSize={9} color="#64748b">
                        -100
                    </Typography>
                    <Typography fontSize={9} color="#64748b">
                        0
                    </Typography>
                    <Typography fontSize={9} color="#64748b">
                        +100
                    </Typography>
                </Box>
            </Box>

            {/* Factor Ratings */}
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2.5}>
                {[
                    { label: "VALUATION", grade: stock.valuation },
                    { label: "GROWTH", grade: stock.growth },
                    { label: "PROFITABILITY", grade: stock.profitability },
                    { label: "MOMENTUM", grade: stock.momentum },
                    { label: "FA", value: stock.quant_rating },
                    { label: "WALL ST", value: stock.wall_st_rating },
                ].map((factor) => (
                    <Box key={factor.label}>
                        <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                            <Typography
                                fontSize={9}
                                fontWeight={700}
                                color="#64748b"
                                letterSpacing="0.05em"
                            >
                                {factor.label}
                            </Typography>
                            <Tooltip title={`${factor.label} score explanation`} arrow>
                                <InfoOutlinedIcon sx={{ fontSize: 10, color: "#475569" }} />
                            </Tooltip>
                            <Typography
                                fontSize="var(--font-size-sm)"
                                fontWeight={700}
                                color={factor.grade ? getColorForGrade(factor.grade) : ratingColor(factor.value)}
                                ml="auto"
                            >
                                {factor.grade || fmtL(factor.value, 1)}
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={factor.grade ? (gradeToScore(factor.grade) / 10) * 100 : (factor.value / 5) * 100}
                            sx={{
                                height: 6,
                                borderRadius: 1,
                                bgcolor: "#0f172a",
                                "& .MuiLinearProgress-bar": {
                                    bgcolor: factor.grade ? getColorForGrade(factor.grade) : ratingColor(factor.value),
                                    borderRadius: 1,
                                },
                            }}
                        />
                    </Box>
                ))}
            </Box>

            {/* Latest Market Narrative */}
            <Box>
                <Box display="flex" alignItems="center" gap={0.75} mb={1.5}>
                    <FiberManualRecordIcon sx={{ fontSize: 6, color: "#3b82f6" }} />
                    <Typography
                        fontSize={9}
                        fontWeight={700}
                        color="#64748b"
                        letterSpacing="0.05em"
                    >
                        LATEST MARKET NARRATIVE
                    </Typography>
                </Box>
                <Stack spacing={1.25}>
                    {[
                        `${stock.company_name} stock shows strong momentum with ${stock.change_percent >= 0 ? 'gains' : 'decline'} of ${fmtL(Math.abs(stock.change_percent), 2)}%`,
                        `Analysts maintain ${getRatingText(stock.wall_st_rating)} rating with price target of ${sym}${fmtL(stock.price * 1.15, 2)}`,
                        `${stock.company_name}'s ${stock.valuation} valuation FA grade reflects current market positioning`,
                    ].map((narrative, idx) => (
                        <Typography
                            key={idx}
                            fontSize="var(--font-size-xs2)"
                            color="#94a3b8"
                            lineHeight={1.5}
                            sx={{
                                cursor: "pointer",
                                transition: "color 0.2s",
                                "&:hover": { color: "#e2e8f0" },
                            }}
                        >
                            {narrative}
                        </Typography>
                    ))}
                </Stack>
            </Box>

            {/* Action Icons */}
            <Box display="flex" gap={1.5} mt={2} pt={2} borderTop="1px solid #2d3748">
                <Box
                    sx={{
                        cursor: "pointer",
                        color: "#64748b",
                        "&:hover": { color: "#3b82f6" },
                    }}
                >
                    <Bookmark size={16} />
                </Box>
                <Box
                    sx={{
                        cursor: "pointer",
                        color: "#64748b",
                        "&:hover": { color: "#f59e0b" },
                    }}
                >
                    <Star size={16} />
                </Box>
                <Box
                    sx={{
                        cursor: "pointer",
                        color: "#64748b",
                        "&:hover": { color: "#10b981" },
                    }}
                >
                    <Share2 size={16} />
                </Box>
            </Box>
        </Card>
    );
};

/* ---------------- main ---------------- */

const TopStocks = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [loading, setLoading] = useState(true);
    const [stocks, setStocks] = useState([]);
    const [total, setTotal] = useState(0);
    const [showFilters, setShowFilters] = useState(false);

    const [filters, setFilters] = useState({
        quantRating: ["STRONG BUY", "BUY"],
        saRating: ["STRONG BUY", "BUY"],
        wallStRating: ["STRONG BUY", "BUY"],
        valuation: ["A+", "A", "A-"],
        growth: ["A+", "A"],
        profitability: ["A+", "A"],
        momentum: ["A+", "A"],
        epsRevisions: ["A+", "A"],
    });

    const [showMore, setShowMore] = useState(false);

    /* -------- derived applied filters -------- */
    const appliedFilters = Object.entries(filters)
        .filter(([, values]) => values.length)
        .map(([key, values]) => ({
            key,
            label: FILTER_LABELS[key],
            values,
        }));

    /* -------- handlers -------- */
    const toggleFilterValue = (key, value) => {
        setFilters((prev) => ({
            ...prev,
            [key]: prev[key].includes(value)
                ? prev[key].filter((v) => v !== value)
                : [...prev[key], value],
        }));
    };

    const clearFilter = (key) => {
        setFilters((prev) => ({ ...prev, [key]: [] }));
    };

    const clearAllFilters = () => {
        const cleared = {};
        Object.keys(filters).forEach((k) => (cleared[k] = []));
        setFilters(cleared);
    };

    /* -------- API -------- */
    useEffect(() => {
        fetchStocks();
    }, []);

    const fetchStocks = async () => {
        setLoading(true);
        const res = await getTopStocks({ page: 1, page_size: 10 });
        if (res?.success) {
            setStocks(res.data?.data || []);
            setTotal(res.data?.total || 0);
        }
        setLoading(false);
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                bgcolor: "#0f172a",
                px: { xs: 2, md: 3 },
                py: 2.5,
            }}
        >
            {/* Header */}
            <Box mb={2.5}>
                <Typography
                    fontSize="var(--font-size-lg)"
                    fontWeight={600}
                    color="#fff"
                    mb={0.25}
                    letterSpacing="-0.01em"
                >
                    Top Rated Stocks
                </Typography>
                <Typography fontSize="var(--font-size-xs2)" color="#64748b" fontWeight={400}
                    sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: showMore ? "unset" : 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                    }}>
                    Find your next top stock pick with FactorAlpha's Top Rated Stocks. This stock
                    screener gives you access to top stock recommendations with the click of a
                    button, while also providing fast access to extended analysis and insight.
                    Each stock is chosen for its 'Strong Buy' or 'Buy' rating from three
                    independent sources (FactorAlpha's industry-leading FA System, independent
                    analysts on FactorAlpha, and Wall Street analysts) and collectively represents
                    the best stocks to buy across the market. Stocks on this list are
                    well-diversified and selected from a broad range of market capitalizations,
                    giving you access to an updated list of smart investing opportunities.                </Typography>
            </Box>

            <Box mt={2} mb={3} position="relative">
                <Typography
                    fontSize="var(--font-size-xs2)"
                    fontWeight={400}
                    sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: showMore ? "unset" : 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        maxWidth: "100%",
                        pr: showMore ? 0 : 10,
                        color: "#64748b",
                    }}
                >
                    Find your next top stock pick with FactorAlpha's Top Rated Stocks. This stock
                    screener gives you access to top stock recommendations with the click of a
                    button, while also providing fast access to extended analysis and insight.
                    Each stock is chosen for its 'Strong Buy' or 'Buy' rating from three
                    independent sources (FactorAlpha's industry-leading FA System, independent
                    analysts on FactorAlpha, and Wall Street analysts) and collectively represents
                    the best stocks to buy across the market. Stocks on this list are
                    well-diversified and selected from a broad range of market capitalizations,
                    giving you access to an updated list of smart investing opportunities.
                </Typography>

                {!showMore && (
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 0,
                            right: 0,
                            display: "flex",
                            alignItems: "center",
                            pl: 3,
                            background: "linear-gradient(to right, transparent, #0f172a 40%)",
                        }}
                    >
                        <Typography
                            onClick={() => setShowMore(true)}
                            sx={{
                                fontSize: {
                                    xs: "0.75rem",
                                    sm: "0.875rem",
                                    md: "1rem",
                                },
                                fontWeight: 500,
                                color: "#3b82f6",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                            }}
                        >
                            Read more
                        </Typography>
                    </Box>
                )}

                {showMore && (
                    <Button
                        variant="text"
                        size="small"
                        onClick={() => setShowMore(false)}
                        sx={{
                            px: 0,
                            mt: 0.5,
                            minWidth: "auto",
                            color: "#3b82f6",
                            fontSize: {
                                xs: "0.75rem",
                                sm: "0.875rem",
                                md: "1rem",
                            },
                            textTransform: "none",
                        }}
                    >
                        Hide
                    </Button>
                )}
            </Box>

            {/* Search Results */}
            <Box sx={{ border: "1px solid #2d3748", borderRadius: 2, p: 2, bgcolor: "#1a2332", mb: 3 }}>
                <Box
                    display="flex"
                    flexDirection={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    gap={1.5}
                >
                    <Typography fontSize={20} color="#fff">
                        Top Stocks: <strong>{total || 46}</strong>
                    </Typography>
                    <Box
                        display="flex"
                        gap={1}
                        width={{ xs: "100%", sm: "fit-content" }}
                        ml={{ sm: "auto" }}
                        justifyContent={{
                            xs: "space-between",
                            sm: "flex-end",
                        }}
                    >
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<TuneIcon />}
                            sx={{
                                textTransform: "none",
                                color: "#3b82f6",
                                borderColor: "#3b82f6",
                                "&:hover": {
                                    borderColor: "#2563eb",
                                    bgcolor: "rgba(59, 130, 246, 0.1)",
                                },
                            }}
                        >
                            Advanced Filters
                        </Button>

                        <Button
                            size="small"
                            variant={showFilters ? "contained" : "outlined"}
                            onClick={() => setShowFilters((p) => !p)}
                            sx={{
                                textTransform: "none",
                                color: showFilters ? "#fff" : "#3b82f6",
                                borderColor: "#3b82f6",
                                backgroundColor: showFilters ? "#3b82f6" : "transparent",
                                "&:hover": !isMobile
                                    ? {
                                        backgroundColor: showFilters
                                            ? "#2563eb"
                                            : "rgba(59, 130, 246, 0.1)",
                                        borderColor: "#2563eb",
                                    }
                                    : {},
                            }}
                        >
                            {showFilters ? "Done" : "Edit Filters"}
                        </Button>
                    </Box>
                </Box>

                <Divider sx={{ my: 2, borderColor: "#2d3748" }} />

                <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={1}>
                    <Box display="flex" gap={1} flexWrap="wrap">
                        {appliedFilters.map(({ key, label }) => (
                            <Box
                                key={key}
                                sx={{
                                    px: 1.2,
                                    py: 0.5,
                                    borderRadius: 1,
                                    background: "#334155",
                                    border: "1px solid #475569",
                                    fontSize: 12,
                                    color: "#e2e8f0",
                                    display: "flex",
                                    gap: 0.8,
                                    alignItems: "center",
                                    cursor: "pointer",
                                }}
                            >
                                {label}
                                <X size={12} onClick={() => clearFilter(key)} />
                            </Box>
                        ))}
                    </Box>

                    <Box display="flex" gap={1.5}>
                        {[Printer, Mail, Link2, X].map((Icon, i) => (
                            <Box
                                key={i}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    background: "#3b82f6",
                                    borderRadius: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    "& svg": { color: "#fff" },
                                    "&:hover": !isMobile ? { background: "#2563eb" } : {},
                                }}
                                onClick={i === 3 ? clearAllFilters : undefined}
                            >
                                <Icon size={16} />
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>

            {/* FILTER EDITOR */}
            {showFilters && (
                <Box sx={{ mt: 3, p: 3, border: "1px solid #2d3748", borderRadius: 2, bgcolor: "#1a2332", mb: 3 }}>
                    <Typography fontWeight={600} mb={2} color="#fff">
                        FA Ratings
                    </Typography>

                    <Box
                        display="grid"
                        gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
                        gap={2}
                        mb={4}
                    >
                        {["quantRating", "saRating", "wallStRating"].map((k) => (
                            <FilterSelect
                                key={k}
                                label={FILTER_LABELS[k]}
                                options={FILTER_OPTIONS[k]}
                                selected={filters[k]}
                                onToggle={(v) => toggleFilterValue(k, v)}
                                onClear={() => clearFilter(k)}
                            />
                        ))}
                    </Box>

                    <Typography fontWeight={600} mb={2} color="#fff">
                        FA Factor Grades
                    </Typography>

                    <Box
                        display="grid"
                        gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
                        gap={2}
                    >
                        {["valuation", "growth", "profitability", "momentum", "epsRevisions"].map(
                            (k) => (
                                <FilterSelect
                                    key={k}
                                    label={FILTER_LABELS[k]}
                                    options={FILTER_OPTIONS[k]}
                                    selected={filters[k]}
                                    onToggle={(v) => toggleFilterValue(k, v)}
                                    onClear={() => clearFilter(k)}
                                />
                            )
                        )}
                    </Box>
                </Box>
            )}

            {/* STOCK CARDS GRID */}
            {loading ? (
                <Box py={6} display="flex" justifyContent="center">
                    <CircularProgress size={28} sx={{ color: "#3b82f6" }} />
                </Box>
            ) : stocks.length === 0 ? (
                <Box
                    py={10}
                    textAlign="center"
                    sx={{
                        bgcolor: "#1a2332",
                        border: "1px solid #2d3748",
                        borderRadius: 2,
                    }}
                >
                    <TrendingUpIcon sx={{ fontSize: 48, color: "#334155", mb: 2 }} />
                    <Typography fontSize="var(--font-size-base)" fontWeight={600} color="#e2e8f0" mb={1}>
                        No Stocks Found
                    </Typography>
                    <Typography fontSize="var(--font-size-sm)" color="#64748b">
                        Adjust your filters to see more results
                    </Typography>
                </Box>
            ) : (
                <Box
                    display="grid"
                    gridTemplateColumns={{
                        xs: "1fr",
                        md: "repeat(2, 1fr)",
                        lg: "repeat(2, 1fr)",
                    }}
                    gap={3}
                >
                    {stocks.map((stock, idx) => (
                        <StockCard key={stock.symbol} stock={stock} rank={idx + 1} />
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default TopStocks;